import React from "react";
import heroImg from "../assets/images/female-infertility/1.jpg";
import coupleImg from "../assets/images/female-infertility/2.jpg";
import Header from "./Header";
import Footer from "./Footer";
import Banner from "./layout/Banner";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// 🎨 Brand Color Palette
const colors = {
  gradientStart: "#546E7A",
  gradientEnd: "#9BB2A3",

  accent: "#D7A19E",
  dark: "#546E7A",
  text: "#4A5A64",

  buttonBg: "#D7A19E",
  buttonHover: "#C5928F",
};

const FemaleInfertility = () => {
  const treatments = [
    {
      title: "Embryo Preservation",
      link: "/female-infertility/embryo-preservation",
      description:
        "Expert care in preserving embryos to safeguard your future parenthood plans. We provide personalized guidance, advanced technology, and ongoing support to protect your fertility and ensure the best outcomes.",
    },
    {
      title: "IUI Treatment",
      link: "/female-infertility/iui-treatment",
      description:
        "Intrauterine Insemination (IUI) is a simple, less invasive fertility treatment. Prepared sperm is placed directly into the uterus during ovulation, increasing the chances of fertilization.",
    },
    {
      title: "Blastocyst Transfer",
      link: "/female-infertility/blastocyst-transfer",
      description:
        "In IVF, embryos are cultured for 5 days until they reach the blastocyst stage, giving a higher chance of successful implantation.",
    },
    {
      title: "Assisted Embryo Hatching",
      link: "/female-infertility/assisted-embryo-hatching",
      description:
        "Assisted Embryo Hatching gently opens the embryo shell to improve implantation chances.",
    },
    {
      title: "PCOD / PCOS Treatment",
      link: "/female-infertility/pcod-pcos-treatment",
      description:
        "PCOD / PCOS is one of the most common causes of infertility. Proper medication and lifestyle guidance help improve ovulation.",
    },
    {
      title: "IVF Treatment",
      link: "/female-infertility/ivf-treatment",
      description:
        "IVF (In Vitro Fertilization) is the most trusted fertility solution for blocked tubes, endometriosis, and unexplained infertility.",
    },
    {
      title: "Infertility Treatment (Comprehensive Care)",
      link: "/infertility-treatment",
      description: {
        text: "Our approach goes beyond just medical procedures. We focus on:",
        points: [
          "Accurate diagnosis",
          "Emotional support",
          "Personalized treatment planning",
          "Holistic care (diet, counseling, and wellness)",
        ],
      },
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Female Infertility Treatment - Dr Vibha Clinic | IVF & Fertility Care
        </title>
        <meta
          name="description"
          content="Expert female infertility treatment at Dr Vibha Clinic including IVF, IUI and PCOS care."
        />
        <meta
          name="keywords"
          content="female infertility, IVF treatment, IUI treatment, PCOS treatment, fertility specialist"
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <Banner
        title="Female Infertility"
        subtitle="Dedicated Care for Women at Every Stage of Life"
        image="https://upload.wikimedia.org/wikipedia/commons/thumb/9/96/A_patient_exam_room_at_an_urgent_care_clinic_and_doctor%E2%80%99s_office_in_North_Carolina%2C_United_States_06.jpg/1280px-A_patient_exam_room_at_an_urgent_care_clinic_and_doctor%E2%80%99s_office_in_North_Carolina%2C_United_States_06.jpg"
      />

      {/* MAIN SECTION */}
      <div
        className="min-h-screen py-20"
        style={{
          background: `linear-gradient(135deg, ${colors.gradientStart}10, white, ${colors.accent}15)`,
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {/* HERO SECTION */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-28">
            <div className="relative order-1 lg:order-2">
              <div
                className="absolute -top-6 -left-6 w-full h-full rounded-3xl"
                style={{ backgroundColor: colors.accent + "40" }}
              ></div>

              <img
                src={heroImg}
                alt="Infertility Treatment"
                className="relative rounded-3xl shadow-2xl w-full object-cover"
              />
            </div>

            <div className="order-2 lg:order-1">
              <span
                className="font-semibold uppercase tracking-widest"
                style={{ color: colors.accent }}
              >
                Female Fertility Care
              </span>

              <h1
                className="text-4xl lg:text-5xl font-bold mt-4 mb-6 leading-tight"
                style={{ color: colors.dark }}
              >
                Advanced Female <br /> Infertility Treatment
              </h1>

              <p
                className="text-lg leading-relaxed mb-8"
                style={{ color: colors.text }}
              >
                Infertility can feel overwhelming, but you are not alone. With
                expert guidance and advanced fertility solutions, we provide
                personalized treatment plans to help you achieve your dream of
                motherhood.
              </p>

              <Link to="/contact-us">
                <button
                  className="text-white px-8 py-4 rounded-xl shadow-lg transition"
                  style={{ backgroundColor: colors.buttonBg }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = colors.buttonHover)
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = colors.buttonBg)
                  }
                >
                  Book Consultation
                </button>
              </Link>
            </div>
          </div>

          {/* UNDERSTANDING SECTION */}
          <div className="bg-white rounded-3xl shadow-xl p-10 lg:p-16 mb-28">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2
                  className="text-3xl lg:text-4xl font-bold mb-6"
                  style={{ color: colors.dark }}
                >
                  Understanding Female Infertility  
                </h2>

                <p className="mb-8 text-lg" style={{ color: colors.text }}>
                  Female infertility refers to difficulty in conceiving even
                  after a year of regular, unprotected intercourse.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    "Ovulation Disorders (PCOS)",
                    "Blocked Fallopian Tubes",
                    "Hormonal Imbalance",
                    "Endometriosis",
                    "Uterine Fibroids",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-4 rounded-xl"
                      style={{ backgroundColor: colors.accent + "20" }}
                    >
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: colors.accent }}
                      ></div>
                      <span style={{ color: colors.text }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <img
                  src={coupleImg}
                  alt="Couple Concerned"
                  className="rounded-3xl shadow-lg w-full"
                />
              </div>
            </div>
          </div>

          {/* TREATMENTS */}
          <div>
            <div className="text-center mb-16">
              <h2
                className="text-3xl lg:text-4xl font-bold mb-4"
                style={{ color: colors.dark }}
              >
                Our Fertility Treatments
              </h2>

              <p style={{ color: colors.text }}>
                Personalized and advanced fertility care designed for you.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-10">
              {treatments.map((item, index) => (
                <div
                  key={index}
                  className="bg-white border p-8 rounded-3xl shadow-md hover:shadow-2xl transition"
                  style={{ borderColor: colors.accent + "40" }}
                >
                  <h3
                    className="text-2xl font-semibold mb-4"
                    style={{ color: colors.accent }}
                  >
                    {item.title}
                  </h3>

                  {typeof item.description === "object" ? (
                    <>
                      <p
                        className="mb-4 leading-relaxed"
                        style={{ color: colors.text }}
                      >
                        {item.description.text}
                      </p>

                      <ul className="space-y-2 list-disc pl-6">
                        {item.description.points.map((point, i) => (
                          <li key={i} style={{ color: colors.text }}>
                            {point}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p style={{ color: colors.text }}>{item.description}</p>
                  )}

                  <Link to={item.link}>
                    <button
                      className="mt-6 font-medium transition"
                      style={{ color: colors.accent }}
                    >
                      Learn More →
                    </button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default FemaleInfertility;
